import React, { useState, useEffect } from 'react';
import { getArbicsxPropIncomes, addArbicsxPropIncome, updateArbicsxPropIncome, deleteArbicsxPropIncome, getArbicsxSettings } from '../../lib/api';
import { useModule } from '../../contexts/ModuleContext';
import { formatCurrency, formatDate, getTodayString, getMonthName } from '../../lib/utils';
import { PropFirmLogo } from '../../lib/propFirmLogos';
import { IdentityColorDot } from '../../lib/identityColors';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Trash2, DollarSign, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function ArbicsxIncassiProp() {
  const { impersonatedUser, isImpersonating } = useModule();
  const [incomes, setIncomes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [openMonths, setOpenMonths] = useState({});
  const [formData, setFormData] = useState({
    date: getTodayString(),
    prop_firm: '',
    identity: '',
    received_amount: ''
  });

  const impersonateId = isImpersonating ? impersonatedUser?.id : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [incomesRes, settingsRes] = await Promise.all([
        getArbicsxPropIncomes(impersonateId, page, 100),
        getArbicsxSettings(impersonateId)
      ]);
      const incomesData = incomesRes.data;
      setIncomes(incomesData.items || []);
      setPagination({ total: incomesData.total || 0, pages: incomesData.pages || 1 });
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [impersonateId]);

  const totalReceived = incomes.reduce((sum, i) => sum + (i.received_amount || 0), 0);
  const totalBonus = incomes.reduce((sum, i) => sum + (i.bonus_15 || 0), 0);

  // Raggruppamento per mese (solo per la presentazione)
  const incomesByMonth = incomes.reduce((acc, i) => {
    const month = i.date?.substring(0, 7) || 'unknown';
    if (!acc[month]) acc[month] = [];
    acc[month].push(i);
    return acc;
  }, {});
  const monthKeys = Object.keys(incomesByMonth).sort().reverse();

  const handleSubmit = async () => {
    if (!formData.date || !formData.received_amount) {
      toast.error('Compila i campi obbligatori');
      return;
    }

    try {
      if (editingIncome) {
        await updateArbicsxPropIncome(editingIncome.id, formData, impersonateId);
        toast.success('Incasso aggiornato');
      } else {
        await addArbicsxPropIncome(formData, impersonateId);
        toast.success('Incasso aggiunto');
      }
      setShowModal(false);
      setEditingIncome(null);
      setFormData({ date: getTodayString(), prop_firm: '', identity: '', received_amount: '' });
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleEdit = (income) => {
    setEditingIncome(income);
    setFormData({
      date: income.date,
      prop_firm: income.prop_firm || '',
      identity: income.identity || '',
      received_amount: income.received_amount?.toString() || ''
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await deleteArbicsxPropIncome(deleteId, impersonateId);
      toast.success('Incasso eliminato');
      setShowDeleteDialog(false);
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const timingText = (income) => {
    if (income.timing_status === 'early' && income.timing_days > 0) {
      return `${income.timing_days} ${income.timing_days === 1 ? 'giorno' : 'giorni'} prima`;
    }
    if (income.timing_status === 'late' && income.timing_days < 0) {
      const d = Math.abs(income.timing_days);
      return `${d} ${d === 1 ? 'giorno' : 'giorni'} di ritardo`;
    }
    return 'In tempo';
  };

  const renderIncomeRows = (list) => (
    <div className="ht-list">
      {list.map(income => (
        <div key={income.id} className="ht-item" data-testid={`income-row-${income.id}`}>
          <div className="ht-item-main">
            <p className="ht-item-t flex items-center gap-2">
              <PropFirmLogo name={income.prop_firm} size="xs" />
              <span className="truncate">{income.prop_firm || '-'}</span>
              {income.payout_type && (
                <span className="ht-tag">{income.payout_type === '48h' ? '48h' : '30gg'}</span>
              )}
            </p>
            <p className="ht-item-s flex items-center gap-1.5">
              <IdentityColorDot identity={income.identity} />
              <span className="truncate">{income.identity || '-'}</span>
              <span className="text-[#5f6672]">· {formatDate(income.date)}</span>
              {income.timing_status && (
                <span className={`flex items-center gap-1 ${
                  income.timing_status === 'early' ? 'ht-pos' : income.timing_status === 'late' ? 'ht-alert' : ''
                }`}>
                  <Clock size={10} />
                  {timingText(income)}
                </span>
              )}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="ht-item-n ht-pos">{formatCurrency(income.received_amount || 0)}</p>
            <p className="ht-item-s ht-num">bonus {formatCurrency(income.bonus_15 || 0)}</p>
          </div>
          <div className="ht-item-actions">
            <button
              type="button"
              onClick={() => handleEdit(income)}
              className="ht-btn ht-btn-icon"
              aria-label="Modifica incasso"
            >
              <Pencil size={13} />
            </button>
            <button
              type="button"
              onClick={() => {
                setDeleteId(income.id);
                setShowDeleteDialog(true);
              }}
              className="ht-btn ht-btn-icon ht-btn-danger"
              aria-label="Elimina incasso"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ht-page-h1">Rimborsi Ricevuti</h1>
          <p className="ht-page-sub">
            Arbicsx {isImpersonating && `- ${impersonatedUser?.name}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingIncome(null);
            setFormData({ date: getTodayString(), prop_firm: '', identity: '', received_amount: '' });
            setShowModal(true);
          }}
          className="ht-btn ht-btn-primary self-start sm:self-auto"
          data-testid="add-income-btn"
        >
          <Plus size={14} />
          Aggiungi Incasso
        </button>
      </div>

      {/* Sintesi */}
      <div className="ht-panel">
        <div className="ht-hero">
          <div className="ht-hero-main">
            <span className="ht-hero-label">Totale ricevuto</span>
            <p className="ht-hero-num ht-pos" data-testid="total-received">{formatCurrency(totalReceived)}</p>
          </div>
          <div className="ht-hero-side ht-rows">
            <div className="ht-row">
              <span className="ht-row-label"><DollarSign size={12} /> Totale bonus 15%</span>
              <span className="ht-row-value" data-testid="total-bonus">{formatCurrency(totalBonus)}</span>
            </div>
            <div className="ht-row">
              <span className="ht-row-label">Incassi registrati</span>
              <span className="ht-row-value">{incomes.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Una card per mese; su telefono si apre e chiude */}
      {incomes.length === 0 ? (
        <div className="ht-panel ht-empty">Nessun incasso registrato</div>
      ) : (
        <div className="space-y-2">
          {monthKeys.map(month => {
            const list = incomesByMonth[month];
            const monthTotal = list.reduce((s, i) => s + (i.received_amount || 0), 0);
            const open = !!openMonths[month];
            return (
              <div key={month} className="ht-acc" data-testid={`income-month-${month}`}>
                <button
                  type="button"
                  className="ht-acc-head"
                  onClick={() => setOpenMonths(prev => ({ ...prev, [month]: !prev[month] }))}
                  data-testid={`income-month-toggle-${month}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <ChevronRight size={14} className={`ht-chev lg:hidden ${open ? 'ht-chev-open' : ''}`} />
                    <span className="ht-acc-title truncate">{getMonthName(month)}</span>
                    <span className="ht-tag">{list.length}</span>
                  </span>
                  <span className="ht-item-n ht-pos">{formatCurrency(monthTotal)}</span>
                </button>
                <div className={`ht-acc-body ${open ? 'block' : 'hidden lg:block'}`}>
                  {renderIncomeRows(list)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="ht-panel text-white">
          <DialogHeader>
            <DialogTitle className="ht-panel-title text-base">
              {editingIncome ? 'Modifica Incasso' : 'Aggiungi Incasso'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Data</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Prop Firm</Label>
              <Select value={formData.prop_firm} onValueChange={(v) => setFormData({ ...formData, prop_firm: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent className="bg-[#11161d] border-[#282c34]">
                  {settings?.prop_firms?.map((p, idx) => (
                    <SelectItem key={idx} value={p.name} className="text-white">{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Identità</Label>
              <Select value={formData.identity} onValueChange={(v) => setFormData({ ...formData, identity: v })}>
                <SelectTrigger className="bg-[#11161d] border-[#282c34] text-white">
                  <SelectValue placeholder="Seleziona" />
                </SelectTrigger>
                <SelectContent className="bg-[#11161d] border-[#282c34]">
                  {settings?.identities?.map((i, idx) => (
                    <SelectItem key={idx} value={i.name} className="text-white">{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-[#8b919c]">Importo Ricevuto ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.received_amount}
                onChange={(e) => setFormData({ ...formData, received_amount: e.target.value })}
                className="bg-[#11161d] border-[#282c34] text-white"
                placeholder="0.00"
              />
              {formData.received_amount && (
                <p className="text-xs text-[#8b919c] ht-num">
                  Bonus 15%: {formatCurrency(parseFloat(formData.received_amount) * 0.15)}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <button type="button" className="ht-btn" onClick={() => setShowModal(false)}>
              Annulla
            </button>
            <button type="button" onClick={handleSubmit} className="ht-btn ht-btn-primary" data-testid="save-income-btn">
              {editingIncome ? 'Aggiorna' : 'Aggiungi'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="ht-panel-title text-base">Eliminare questo incasso?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-[#8b919c]">
              Questa azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="ht-btn border-0">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="ht-btn ht-btn-danger">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
